/**
 * Baking an unbounded drawing into square tiles.
 *
 * The old drawing pad baked every finished stroke into one screen-sized layer,
 * which works right up until the paper is bigger than the screen. Rebuilding
 * that layer on every pan frame would restroke hundreds of paths sixty times a
 * second, so instead the plane is cut into fixed tiles: a tile is drawn once,
 * reused while it stays on screen, and thrown away only when a stroke lands on
 * it or the zoom settles somewhere new.
 *
 * During a pinch the existing tiles are simply blitted at the wrong scale —
 * momentarily soft, but free. They are re-baked crisply when the fingers lift,
 * because re-baking mid-gesture is what turns a smooth pinch into a slideshow.
 */

import type { Stroke } from './stroke.ts';
import type { Box, Camera } from '../lib/scene.ts';
import { boxesOverlap, strokeBox, toScreen } from '../lib/scene.ts';
import { drawStroke } from './render.ts';

/** Tile edge in screen pixels at the scale it was baked. */
const TILE = 512;
/** Tiles kept alive off-screen before being reclaimed. */
const MAX_TILES = 80;

interface Tile {
  canvas: HTMLCanvasElement;
  usedAt: number;
}

export interface Baked {
  stroke: Stroke;
  box: Box;
}

export function bakedOf(stroke: Stroke): Baked {
  return { stroke, box: strokeBox(stroke) };
}

export class TileLayer {
  private tiles = new Map<string, Tile>();
  /** The zoom the current tiles were drawn at. */
  private bakedScale = 1;
  private dpr = 1;
  private clock = 0;

  setDpr(dpr: number): void {
    if (dpr === this.dpr) return;
    this.dpr = dpr;
    this.clear();
  }

  clear(): void {
    this.tiles.clear();
  }

  /** Drop every tile a new (or removed) stroke touches. */
  invalidate(box: Box): void {
    const world = TILE / this.bakedScale;
    const c0 = Math.floor(box.minX / world);
    const c1 = Math.floor(box.maxX / world);
    const r0 = Math.floor(box.minY / world);
    const r1 = Math.floor(box.maxY / world);
    for (let c = c0; c <= c1; c++) {
      for (let r = r0; r <= r1; r++) this.tiles.delete(`${c},${r}`);
    }
  }

  /**
   * Re-bake at the camera's current zoom. Called when a gesture ends, so the
   * picture is sharp again the moment the fingers leave the glass.
   */
  settle(scale: number): void {
    if (Math.abs(scale - this.bakedScale) < 1e-6) return;
    this.bakedScale = scale;
    this.clear();
  }

  private bake(col: number, row: number, strokes: Baked[]): Tile {
    const world = TILE / this.bakedScale;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(TILE * this.dpr);
    canvas.height = Math.round(TILE * this.dpr);
    const ctx = canvas.getContext('2d');
    const tile: Tile = { canvas, usedAt: this.clock };
    if (!ctx) return tile;

    const box: Box = {
      minX: col * world,
      minY: row * world,
      maxX: (col + 1) * world,
      maxY: (row + 1) * world,
    };
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.scale(this.bakedScale, this.bakedScale);
    ctx.translate(-box.minX, -box.minY);
    for (const b of strokes) {
      if (boxesOverlap(b.box, box)) drawStroke(ctx, b.stroke);
    }
    return tile;
  }

  private reclaim(): void {
    if (this.tiles.size <= MAX_TILES) return;
    const entries = [...this.tiles.entries()].sort((a, b) => a[1].usedAt - b[1].usedAt);
    for (let i = 0; i < entries.length - MAX_TILES; i++) this.tiles.delete(entries[i][0]);
  }

  /** Blit every tile the camera can see, baking the ones that do not exist yet. */
  draw(ctx: CanvasRenderingContext2D, cam: Camera, width: number, height: number, strokes: Baked[]): void {
    this.clock++;
    const world = TILE / this.bakedScale;
    const halfW = width / 2 / cam.scale;
    const halfH = height / 2 / cam.scale;
    const c0 = Math.floor((cam.x - halfW) / world);
    const c1 = Math.floor((cam.x + halfW) / world);
    const r0 = Math.floor((cam.y - halfH) / world);
    const r1 = Math.floor((cam.y + halfH) / world);

    for (let c = c0; c <= c1; c++) {
      for (let r = r0; r <= r1; r++) {
        const key = `${c},${r}`;
        let tile = this.tiles.get(key);
        if (!tile) {
          tile = this.bake(c, r, strokes);
          this.tiles.set(key, tile);
        }
        tile.usedAt = this.clock;

        // Round both edges so neighbouring tiles share an exact boundary —
        // rounding width instead of the far edge leaves hairline seams.
        const a = toScreen(cam, c * world, r * world, width, height);
        const b = toScreen(cam, (c + 1) * world, (r + 1) * world, width, height);
        const x = Math.round(a.x);
        const y = Math.round(a.y);
        const w = Math.round(b.x) - x;
        const h = Math.round(b.y) - y;
        if (w > 0 && h > 0) ctx.drawImage(tile.canvas, x, y, w, h);
      }
    }

    this.reclaim();
  }
}
